import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft } from 'react-icons/fi';
import { useAuth } from '../context/AuthContext';
import API from '../api/axios';
import toast from 'react-hot-toast';
import ServiceGrid from '../components/ServiceGrid';
import PrintStoreLanding from '../components/PrintStoreLanding';
import PrintOrderConfig from '../components/PrintOrderConfig';
import { formatPrice } from '../utils/constants';
import './PrintStore.css';

const MAX_FILES = 15;
const MAX_SIZE_MB = 50;
const ALLOWED_EXT = ['.pdf', '.jpg', '.jpeg', '.png', '.webp', '.heic', '.heif', '.doc', '.docx', '.ppt', '.pptx'];

const PrintStore = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { cart, cartTotal } = useAuth();
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploadedFiles, setUploadedFiles] = useState([]);
  const [showConfig, setShowConfig] = useState(false);
  const [uploading, setUploading] = useState(false);

  useEffect(() => {
    if (location.state?.editFile) {
      setUploadedFiles([location.state.editFile]);
      setShowConfig(true);
    }
  }, [location.state]);

  useEffect(() => {
    const fetchServices = async () => {
      try {
        const { data } = await API.get('/print-services');
        setServices(data.services || []);
      } catch (err) {
        console.error('Failed to load print services:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchServices();
  }, []);

  const handleFilesUploaded = (files) => {
    setUploadedFiles(files);
    if (files.length > 0) setShowConfig(true);
  };

  const handleAddMoreFiles = async (newFiles) => {
    const remaining = MAX_FILES - uploadedFiles.length;
    if (remaining <= 0) {
      toast.error(`Maximum ${MAX_FILES} files allowed`);
      return;
    }

    // Deduplicate: build a set of existing file identifiers (name + size)
    const existingKeys = new Set(uploadedFiles.map(f => `${f.name}|${f.size}`));

    // Filter out files that are already uploaded
    const notDuplicate = newFiles.filter(f => !existingKeys.has(`${f.name}|${f.size}`));
    if (notDuplicate.length === 0) {
      toast.info('Selected file(s) already uploaded');
      return;
    }

    // Apply remaining slot limit
    const limited = notDuplicate.slice(0, remaining);

    const valid = limited.filter(file => {
      const ext = '.' + file.name.split('.').pop().toLowerCase();
      if (!ALLOWED_EXT.includes(ext)) { toast.error(`${file.name}: Unsupported type`); return false; }
      if (file.size > MAX_SIZE_MB * 1024 * 1024) { toast.error(`${file.name}: Exceeds ${MAX_SIZE_MB}MB`); return false; }
      return true;
    });

    if (valid.length === 0) return;

    setUploading(true);
    try {
      const formData = new FormData();
      valid.forEach(f => formData.append('files', f));
      const { data } = await API.post('/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
        timeout: 120000
      });
      const enriched = (data.files || []).map((f, idx) => ({
        ...f,
        file: valid[idx],
        localFile: valid[idx]
      }));
      const combined = [...uploadedFiles, ...enriched];
      setUploadedFiles(combined);
      toast.success(`${data.files.length} more file(s) added!`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Upload failed');
    } finally {
      setUploading(false);
    }
  };

  // Show full-screen print config when files are ready
  if (showConfig && uploadedFiles.length > 0) {
    return (
      <PrintOrderConfig
        uploadedFiles={uploadedFiles}
        onAddMoreFiles={handleAddMoreFiles}
        onBack={() => setShowConfig(false)}
        initialConfigs={location.state?.editFile?.config ? [location.state.editFile.config] : null}
        editCartItem={location.state?.editCartItem || null}
      />
    );
  }

  return (
    <div className="page print-store-page">
      <div className="page-top-bar">
        <button className="back-btn" onClick={() => navigate('/')}>
          <FiArrowLeft />
        </button>
        <h1>Print Store</h1>
      </div>

      {/* PrintStore Landing Content */}
      <PrintStoreLanding onFilesUploaded={handleFilesUploaded} />

      {/* Print Services */}
      {loading ? (
        <div className="spinner"></div>
      ) : (
        <ServiceGrid services={services} />
      )}

      {/* Floating Cart Bar */}
      {cart.length > 0 && (
        <div className="floating-cart" onClick={() => navigate('/orders')}>
          <div className="floating-cart-info">
            <span className="floating-cart-count">{cart.length} item(s)</span>
            <span className="floating-cart-total">{formatPrice(cartTotal)}</span>
          </div>
          <span className="floating-cart-action">View Cart →</span>
        </div>
      )}

      <div className="fab-spacer"></div>
    </div>
  );
};

export default PrintStore;
